import type {
  ActiveSession,
  InspectionsReport,
  LegalStatus,
  AcceptLegalRequest,
  TenantLegalAcceptanceRow,
  RecordOfflineAcceptanceRequest,
  DocumentEmailPreview,
  DocumentEmailSummary,
  Customer,
  CustomerDetail,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  ListCustomersResult,
  Vehicle,
  CreateVehicleRequest,
  UpdateVehicleRequest,
  ListVehiclesResult,
  DecodeVinResult,
  DecodePlateResult,
  VehicleMakeOption,
  VehicleModelOption,
  VehicleModelYearOption,
  VehicleTrimOption,
  ListPartsResult,
  PredefinedItem,
  CreatePredefinedItemRequest,
  UpdatePredefinedItemRequest,
  ListPredefinedItemsResult,
  Technician,
  CreateTechnicianRequest,
  UpdateTechnicianRequest,
  ListTechniciansResult,
  WorkOrderAggregate,
  CreateWorkOrderRequest,
  UpdateWorkOrderRequest,
  UpdateInternalNotesRequest,
  AddPartLineRequest,
  UpdatePartLineRequest,
  AddLaborLineRequest,
  UpdateLaborLineRequest,
  AddSubletLineRequest,
  UpdateSubletLineRequest,
  WorkOrderTechnicianRole,
  ListWorkOrdersResult,
  InvoiceWithLines,
  InvoiceSummary,
  ListInvoicesResult,
  RecordPaymentRequest,
  PaymentSummary,
  TenantSettings,
  UpdateTenantSettingsRequest,
  ShopProfile,
  UpdateShopProfileRequest,
  PdfTemplate,
  SearchResponse,
  DashboardSummary,
  PaymentsReceivedReport,
  LoginResult,
  InvoiceDocumentKind,
  EnableTotpResult,
  ConfirmTotpResult,
  TrustedDeviceSummary,
  CreateTenantRequest,
  CreateTenantResult,
  ListTenantsResult,
  ResetTenantPasswordResult,
  SetTenantThemeRequest,
  VehicleCatalogRefreshResult,
  CreateAppSupportInquiryRequest,
  SupportInquiry,
  SupportInquiryDetail,
  ListSupportInquiriesResult,
  ReplySupportInquiryRequest,
  SupportUnreadTotal,
} from "@autorepair/contracts";

/** Read a cookie's raw value by name — used only for the CSRF double-submit
 * token, which is deliberately NOT HttpOnly so this works. See
 * apps/api/src/auth/csrf.ts. */
function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1];
}

/**
 * Every API call and every PDF-iframe/download URL must be relative to
 * Vite's configured `base` (set via VITE_BASE — see vite.config.ts), not
 * the domain root: a root-absolute path like `fetch("/customers")` ignores
 * whatever path prefix the page was actually served under (e.g.
 * `https://aqeel.myqnapcloud.com/ar/...`), silently hitting the DOMAIN'S
 * root instead — invisible in local dev (base is "/", so this is a no-op)
 * and total once deployed behind a path prefix. `import.meta.env.BASE_URL`
 * is Vite's own normalized reflection of `base` (e.g. "/ar/" or "/").
 */
const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
export function withBase(path: string): string {
  return `${BASE}${path}`;
}

/** Every real backend route lives under /api (see apps/api/src/main.ts's
 * setGlobalPrefix and docs/TRAPS.md #63) — this is distinct from
 * withBase()'s deployment-location prefix above, and the two stack:
 * `${BASE}/api${path}`. Never call this for a genuinely static asset
 * (favicon, login background) — those go through withBase() directly. */
export function apiUrl(path: string): string {
  return withBase(`/api${path}`);
}

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/** AuthProvider registers itself here so that ANY request — not just the
 * one-time mount check in auth.tsx — can notice the session has gone
 * invalid (expired, revoked, or the API process restarted) and drop back
 * to a logged-out state, which sends `Protected` to /login instead of
 * whatever page happened to render a 401 as a generic error banner. */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (res.status === 304) return undefined as T;

  const text = await res.text();
  const parsed = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    const message =
      (parsed && typeof parsed === "object" && "message" in parsed && typeof parsed.message === "string"
        ? parsed.message
        : undefined) ?? `Request failed (${res.status})`;
    if (res.status === 401) onUnauthorized?.();
    throw new ApiError(res.status, message, parsed);
  }
  return parsed as T;
}

/** Every non-GET request needs the CSRF header echoed back from the
 * cookie, or the API's guard rejects it with a blanket 403 — see
 * docs/NEW_CHAT_PROMPT.md trap #25. */
async function request<T>(path: string, options: { method?: string; body?: unknown } = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = {};
  let body: string | undefined;
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }
  if (method !== "GET") {
    const csrf = readCookie("ar_csrf");
    if (csrf) headers["x-csrf-token"] = csrf;
  }

  const res = await fetch(apiUrl(path), { method, headers, body, credentials: "same-origin" });
  return handleResponse<T>(res);
}

/** Same CSRF requirement as request() above, but for a multipart file
 * upload — FormData needs no Content-Type set by hand (the browser adds
 * its own multipart boundary), which is the one thing that keeps this a
 * separate function rather than a `request()` option. */
async function uploadFile<T>(path: string, file: File): Promise<T> {
  const headers: Record<string, string> = {};
  const csrf = readCookie("ar_csrf");
  if (csrf) headers["x-csrf-token"] = csrf;
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(apiUrl(path), { method: "POST", headers, body: formData, credentials: "same-origin" });
  return handleResponse<T>(res);
}

function qs(params: Record<string, string | number | boolean | string[] | undefined>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    if (Array.isArray(value)) {
      for (const v of value) usp.append(key, v);
    } else {
      usp.append(key, String(value));
    }
  }
  const query = usp.toString();
  return query ? `?${query}` : "";
}

// ---- Auth ----

export const authApi = {
  login: (email: string, password: string) => request<LoginResult>("/auth/login", { method: "POST", body: { email, password } }),
  selectTenant: (tenantId: number) => request<LoginResult>("/auth/select-tenant", { method: "POST", body: { tenantId } }),
  changePasswordFirstLogin: (newPassword: string) =>
    request<LoginResult>("/auth/change-password-first-login", { method: "POST", body: { newPassword } }),
  logout: () => request<{ ok: true }>("/auth/logout", { method: "POST" }),
  // Revokes every session for this user, including the current one — see
  // AuthController.logoutAllDevices's own doc comment.
  logoutAllDevices: () => request<{ ok: true }>("/auth/logout-all-devices", { method: "POST" }),
  session: () => request<{ session: ActiveSession }>("/auth/session"),
  updateProfile: (fullName: string, email?: string) =>
    request<{ fullName: string; email: string }>("/auth/profile", { method: "PATCH", body: { fullName, email } }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>("/auth/change-password", { method: "POST", body: { currentPassword, newPassword } }),
  verifyTotp: (code: string, rememberDevice: boolean) =>
    request<LoginResult>("/auth/totp/verify", { method: "POST", body: { code, rememberDevice } }),
  enableTotp: () => request<EnableTotpResult>("/auth/totp/enable", { method: "POST" }),
  confirmTotp: (code: string) => request<ConfirmTotpResult>("/auth/totp/confirm", { method: "POST", body: { code } }),
  disableTotp: (currentPassword: string, code: string) =>
    request<{ ok: true }>("/auth/totp/disable", { method: "POST", body: { currentPassword, code } }),
  regenerateBackupCodes: () =>
    request<{ codes: string[] }>("/auth/totp/backup-codes/regenerate", { method: "POST" }),
  listTrustedDevices: () => request<{ devices: TrustedDeviceSummary[] }>("/auth/totp/trusted-devices"),
  revokeTrustedDevice: (id: number) =>
    request<{ ok: true }>(`/auth/totp/trusted-devices/${id}`, { method: "DELETE" }),
};

// ---- Admin (platform-admin only) ----

export const adminApi = {
  listTenants: () => request<ListTenantsResult>("/admin/tenants"),
  createTenant: (body: CreateTenantRequest) => request<CreateTenantResult>("/admin/tenants", { method: "POST", body }),
  lockTenant: (tenantId: number) => request<{ ok: true }>(`/admin/tenants/${tenantId}/lock`, { method: "POST" }),
  unlockTenant: (tenantId: number) => request<{ ok: true }>(`/admin/tenants/${tenantId}/unlock`, { method: "POST" }),
  resetTenantPassword: (tenantId: number) =>
    request<ResetTenantPasswordResult>(`/admin/tenants/${tenantId}/reset-password`, { method: "POST" }),
  disableTenantTotp: (tenantId: number) =>
    request<{ ok: true }>(`/admin/tenants/${tenantId}/disable-2fa`, { method: "POST" }),
  setTenantTheme: (tenantId: number, body: SetTenantThemeRequest) =>
    request<{ ok: true }>(`/admin/tenants/${tenantId}/theme`, { method: "POST", body }),
  refreshVehicleCatalog: (file: File) =>
    uploadFile<VehicleCatalogRefreshResult>("/admin/vehicle-catalog/refresh", file),
  listSupportInquiries: (params: { status?: string; source?: string; limit?: number; offset?: number } = {}) =>
    request<ListSupportInquiriesResult>(`/admin/support-inquiries${qs(params)}`),
  getSupportInquiry: (id: number) => request<{ inquiry: SupportInquiryDetail }>(`/admin/support-inquiries/${id}`),
  replySupportInquiry: (id: number, body: ReplySupportInquiryRequest) =>
    request<{ inquiry: SupportInquiryDetail }>(`/admin/support-inquiries/${id}/reply`, { method: "POST", body }),
  unreadSupportCount: () => request<SupportUnreadTotal>("/admin/support-inquiries/unread-count"),
  tenantLegal: (tenantId: number) => request<{ items: TenantLegalAcceptanceRow[] }>(`/admin/tenants/${tenantId}/legal`),
  recordOfflineLegalAcceptance: (tenantId: number, body: RecordOfflineAcceptanceRequest) =>
    request<{ ok: true }>(`/admin/tenants/${tenantId}/legal/offline`, { method: "POST", body }),
};

// ---- Legal (platform Terms/Privacy/DPA consent) ----

export const legalApi = {
  status: () => request<LegalStatus>("/legal/status"),
  accept: (body: AcceptLegalRequest) => request<{ ok: true }>("/legal/accept", { method: "POST", body }),
};

// ---- Customers ----

export const customersApi = {
  list: (
    params: {
      search?: string;
      includeInactive?: boolean;
      sortBy?: "createdAt" | "displayName";
      sortDirection?: "asc" | "desc";
      limit?: number;
      offset?: number;
    } = {},
  ) => request<ListCustomersResult>(`/customers${qs(params)}`),
  get: (id: number) => request<{ customer: CustomerDetail }>(`/customers/${id}`),
  getByNumber: (customerNumber: number) => request<{ customer: CustomerDetail }>(`/customers/by-number/${customerNumber}`),
  create: (body: CreateCustomerRequest) => request<{ customer: Customer }>("/customers", { method: "POST", body }),
  update: (id: number, body: UpdateCustomerRequest) =>
    request<{ customer: Customer }>(`/customers/${id}`, { method: "PATCH", body }),
  deactivate: (id: number) => request<{ customer: Customer }>(`/customers/${id}/deactivate`, { method: "POST" }),
  activate: (id: number) => request<{ customer: Customer }>(`/customers/${id}/activate`, { method: "POST" }),
  delete: (id: number) => request<{ ok: true }>(`/customers/${id}`, { method: "DELETE" }),
};

// ---- Vehicles ----

export const vehiclesApi = {
  list: (params: { customerId?: number; search?: string; includeInactive?: boolean; limit?: number; offset?: number } = {}) =>
    request<ListVehiclesResult>(`/vehicles${qs(params)}`),
  get: (id: number) => request<{ vehicle: Vehicle }>(`/vehicles/${id}`),
  create: (body: CreateVehicleRequest) => request<{ vehicle: Vehicle }>("/vehicles", { method: "POST", body }),
  update: (id: number, body: UpdateVehicleRequest) =>
    request<{ vehicle: Vehicle }>(`/vehicles/${id}`, { method: "PATCH", body }),
  delete: (id: number) => request<{ ok: true }>(`/vehicles/${id}`, { method: "DELETE" }),
  // The backend permanently caches full 17-char VIN decodes (vin_decodes
  // table) and always checks that cache first — calling this again for
  // the same VIN never re-hits NHTSA, so there's nothing extra to build
  // on the frontend to satisfy "don't look the same VIN up twice."
  decodeVin: (vin: string) => request<DecodeVinResult>("/vehicles/decode-vin", { method: "POST", body: { vin } }),
  decodePlate: (plate: string, state: string) =>
    request<DecodePlateResult>("/vehicles/decode-plate", { method: "POST", body: { plate, state } }),
};

// ---- Vehicle reference (Year/Make/Model/Trim picker) ----

export const vehicleReferenceApi = {
  listMakes: () => request<{ makes: VehicleMakeOption[] }>("/vehicle-reference/makes"),
  listModels: (makeId: number) => request<{ models: VehicleModelOption[] }>(`/vehicle-reference/makes/${makeId}/models`),
  listYears: (modelId: number) => request<{ years: VehicleModelYearOption[] }>(`/vehicle-reference/models/${modelId}/years`),
  listTrims: (modelId: number, year: number) =>
    request<{ trims: VehicleTrimOption[] }>(`/vehicle-reference/models/${modelId}/trims${qs({ year })}`),
};

// ---- Parts ----

export const partsApi = {
  list: (params: { search?: string; includeInactive?: boolean; limit?: number; offset?: number } = {}) =>
    request<ListPartsResult>(`/parts${qs(params)}`),
};

// ---- Predefined items ----

export const predefinedItemsApi = {
  list: (params: { type?: "part" | "labor" | "sublet"; search?: string; includeInactive?: boolean; limit?: number; offset?: number } = {}) =>
    request<ListPredefinedItemsResult>(`/predefined-items${qs(params)}`),
  create: (body: CreatePredefinedItemRequest) => request<{ item: PredefinedItem }>("/predefined-items", { method: "POST", body }),
  update: (id: number, body: UpdatePredefinedItemRequest) =>
    request<{ item: PredefinedItem }>(`/predefined-items/${id}`, { method: "PATCH", body }),
  delete: (id: number) => request<{ ok: true }>(`/predefined-items/${id}`, { method: "DELETE" }),
};

// ---- Technicians ----

export const techniciansApi = {
  list: (params: { search?: string; includeInactive?: boolean; limit?: number; offset?: number } = {}) =>
    request<ListTechniciansResult>(`/technicians${qs(params)}`),
  get: (id: number) => request<{ technician: Technician }>(`/technicians/${id}`),
  create: (body: CreateTechnicianRequest) => request<{ technician: Technician }>("/technicians", { method: "POST", body }),
  update: (id: number, body: UpdateTechnicianRequest) =>
    request<{ technician: Technician }>(`/technicians/${id}`, { method: "PATCH", body }),
  deactivate: (id: number) => request<{ technician: Technician }>(`/technicians/${id}/deactivate`, { method: "POST" }),
  activate: (id: number) => request<{ technician: Technician }>(`/technicians/${id}/activate`, { method: "POST" }),
  delete: (id: number) => request<{ ok: true }>(`/technicians/${id}`, { method: "DELETE" }),
};

// ---- Work orders ----

export const workOrdersApi = {
  list: (
    params: {
      status?: string | string[];
      invoiceStatus?: string | string[];
      customerId?: number;
      vehicleId?: number;
      technicianId?: number;
      search?: string;
      includeVoid?: boolean;
      sortBy?: string;
      sortDirection?: "asc" | "desc";
      limit?: number;
      offset?: number;
    } = {},
  ) => request<ListWorkOrdersResult>(`/work-orders${qs(params)}`),
  get: (id: number) => request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}`),
  getByNumber: (workOrderNumber: number) => request<{ aggregate: WorkOrderAggregate }>(`/work-orders/by-number/${workOrderNumber}`),
  create: (body: CreateWorkOrderRequest) => request<{ aggregate: WorkOrderAggregate }>("/work-orders", { method: "POST", body }),
  update: (id: number, body: UpdateWorkOrderRequest) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}`, { method: "PATCH", body }),
  updateInternalNotes: (id: number, body: UpdateInternalNotesRequest) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/internal-notes`, { method: "PATCH", body }),
  approve: (id: number, version: number) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/approve`, { method: "POST", body: { version } }),
  start: (id: number, version: number) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/start`, { method: "POST", body: { version } }),
  complete: (id: number, version: number) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/complete`, { method: "POST", body: { version } }),
  void: (id: number, version: number, reason: string) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/void`, { method: "POST", body: { version, reason } }),
  unvoid: (id: number, version: number) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/unvoid`, { method: "POST", body: { version } }),
  delete: (id: number, version: number) => request<{ ok: true }>(`/work-orders/${id}`, { method: "DELETE", body: { version } }),
  issueEstimate: (id: number, version: number) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/estimate`, { method: "POST", body: { version } }),
  estimateEmailPreview: (id: number) => request<DocumentEmailPreview>(`/work-orders/${id}/estimate/email-preview`),
  estimateEmailSummary: (id: number) => request<DocumentEmailSummary>(`/work-orders/${id}/estimate/emails`),
  emailEstimate: (id: number) => request<{ sentTo: string }>(`/work-orders/${id}/estimate/email`, { method: "POST" }),
  estimateDocumentUrl: (id: number, download = false) =>
    apiUrl(`/work-orders/${id}/estimate/document${download ? "?download=1" : ""}`),
  issueInvoice: (id: number, version: number) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/invoices`, { method: "POST", body: { version } }),
  addPartLine: (id: number, body: AddPartLineRequest) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/part-lines`, { method: "POST", body }),
  updatePartLine: (id: number, lineId: number, body: UpdatePartLineRequest) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/part-lines/${lineId}`, { method: "PATCH", body }),
  deletePartLine: (id: number, lineId: number, version: number) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/part-lines/${lineId}`, { method: "DELETE", body: { version } }),
  addLaborLine: (id: number, body: AddLaborLineRequest) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/labor-lines`, { method: "POST", body }),
  updateLaborLine: (id: number, lineId: number, body: UpdateLaborLineRequest) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/labor-lines/${lineId}`, { method: "PATCH", body }),
  deleteLaborLine: (id: number, lineId: number, version: number) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/labor-lines/${lineId}`, {
      method: "DELETE",
      body: { version },
    }),
  assignTechnician: (id: number, version: number, technicianId: number, role: WorkOrderTechnicianRole = "primary") =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/technicians`, {
      method: "POST",
      body: { version, technicianId, role },
    }),
  unassignTechnician: (id: number, technicianId: number, version: number) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/technicians/${technicianId}`, {
      method: "DELETE",
      body: { version },
    }),
  addSubletLine: (id: number, body: AddSubletLineRequest) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/sublet-lines`, { method: "POST", body }),
  updateSubletLine: (id: number, lineId: number, body: UpdateSubletLineRequest) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/sublet-lines/${lineId}`, { method: "PATCH", body }),
  deleteSubletLine: (id: number, lineId: number, version: number) =>
    request<{ aggregate: WorkOrderAggregate }>(`/work-orders/${id}/sublet-lines/${lineId}`, {
      method: "DELETE",
      body: { version },
    }),
};

// ---- Invoices ----

export const invoicesApi = {
  list: (
    params: {
      status?: string | string[];
      customerId?: number;
      workOrderId?: number;
      search?: string;
      onlyArchived?: boolean;
      sortBy?: string;
      sortDirection?: "asc" | "desc";
      limit?: number;
      offset?: number;
    } = {},
  ) => request<ListInvoicesResult>(`/invoices${qs(params)}`),
  get: (id: number) => request<InvoiceWithLines>(`/invoices/${id}`),
  email: (id: number) => request<{ sentTo: string }>(`/invoices/${id}/email`, { method: "POST" }),
  emailPreview: (id: number) => request<DocumentEmailPreview>(`/invoices/${id}/email-preview`),
  emailSummary: (id: number) => request<DocumentEmailSummary>(`/invoices/${id}/emails`),
  getByNumber: (invoiceNumber: number) => request<InvoiceWithLines>(`/invoices/by-number/${invoiceNumber}`),
  void: (id: number, reason: string) => request<{ invoice: InvoiceSummary }>(`/invoices/${id}/void`, { method: "POST", body: { reason } }),
  archive: (id: number) => request<{ invoice: InvoiceSummary }>(`/invoices/${id}/archive`, { method: "POST" }),
  unarchive: (id: number) => request<{ invoice: InvoiceSummary }>(`/invoices/${id}/unarchive`, { method: "POST" }),
  delete: (id: number) => request<{ ok: true }>(`/invoices/${id}/delete`, { method: "POST" }),
  documentUrl: (id: number, kind: InvoiceDocumentKind, download = false) =>
    apiUrl(`/invoices/${id}/documents/${kind}${download ? "?download=1" : ""}`),
};

// ---- Payments ----

export const paymentsApi = {
  record: (invoiceId: number, body: RecordPaymentRequest) =>
    request<{ payment: PaymentSummary }>(`/invoices/${invoiceId}/payments`, { method: "POST", body }),
  void: (paymentId: number, reason: string) =>
    request<{ payment: PaymentSummary }>(`/payments/${paymentId}/void`, {
      method: "POST",
      body: { reason },
    }),
  unvoid: (paymentId: number) => request<{ payment: PaymentSummary }>(`/payments/${paymentId}/unvoid`, { method: "POST" }),
  delete: (paymentId: number) => request<{ ok: true }>(`/payments/${paymentId}`, { method: "DELETE" }),
};

// ---- Tenant settings ----

export const tenantSettingsApi = {
  get: () => request<{ settings: TenantSettings }>("/tenant-settings"),
  update: (body: UpdateTenantSettingsRequest) =>
    request<{ settings: TenantSettings }>("/tenant-settings", { method: "PATCH", body }),
};

export const shopProfileApi = {
  get: () => request<{ shopProfile: ShopProfile }>("/shop-profile"),
  update: (body: UpdateShopProfileRequest) =>
    request<{ shopProfile: ShopProfile }>("/shop-profile", { method: "PATCH", body }),
  uploadLogo: (file: File) => uploadFile<{ shopProfile: ShopProfile }>("/shop-profile/logo", file),
  removeLogo: () => request<{ shopProfile: ShopProfile }>("/shop-profile/logo", { method: "DELETE" }),
  // Same plain-link pattern as invoicesApi.documentUrl — renders a sample
  // invoice in the given template without saving anything (see
  // ShopProfileController.pdfPreview). taxLabel/taxRegNumber are the
  // Settings FORM's own current values (may be unsaved) so a Canadian
  // shop can preview "HST"/their registration number before hitting Save.
  pdfPreviewUrl: (template: PdfTemplate, taxLabel?: string, taxRegNumber?: string | null) => {
    const params = new URLSearchParams({ template });
    if (taxLabel) params.set("taxLabel", taxLabel);
    if (taxRegNumber) params.set("taxRegNumber", taxRegNumber);
    return apiUrl(`/shop-profile/pdf-preview?${params.toString()}`);
  },
};

// ---- Search & dashboard ----

export const searchApi = {
  search: (q: string) => request<SearchResponse>(`/search${qs({ q })}`),
};

export const dashboardApi = {
  summary: () => request<DashboardSummary>("/dashboard"),
};

export const inspectionsApi = {
  /** `from`/`to` are inclusive YYYY-MM-DD inspection dates; "" leaves that side open. */
  list: (range: { from: string; to: string }) =>
    request<InspectionsReport>(`/inspections${qs({ from: range.from, to: range.to })}`),
};

export const reportsApi = {
  /** `from`/`to` are inclusive YYYY-MM-DD dates; "" leaves that side open. */
  paymentsReceived: (range: { from: string; to: string }) =>
    request<PaymentsReceivedReport>(`/reports/payments-received${qs({ from: range.from, to: range.to })}`),
};

// ---- Support (in-app "Contact support" page only — the public,
// anonymous bay2bill-web contact form calls the API directly, not through
// this client) ----

export const supportApi = {
  submitFromApp: (body: CreateAppSupportInquiryRequest) =>
    request<{ inquiry: SupportInquiry }>("/support/inquiries/app", { method: "POST", body }),
};
